import React from "react";
import {
  Badge,
  Box,
  Button,
  IconButton,
  Popover,
  TextField,
  Tooltip,
  Typography,
  CircularProgress as MuiCircularProgress,
} from "@mui/material";
import { LiveHelp } from "@mui/icons-material";
import ReCAPTCHA from "react-google-recaptcha";
import Joi from "joi";
import { VIDEO_LIBRARY_CHAT_EMAIL } from "../../constants/util";
import { sendEmailFromVideoLibrary } from "../../api/apiCalls";

const schema = Joi.object({
  email: Joi.string()
    .trim()
    .email({ tlds: { allow: false } })
    .required()
    .messages({
      'string.email': 'This email is not a valid format',
      'string.empty': 'E-mail is required',
      'any.required': 'E-mail is required'
    }),
  message: Joi.string()
    .trim()
    .required()
    .messages({
      'string.empty': 'Message is required',
      'any.required': 'Message is required'
    }),
  captcha: Joi.string()
    .trim()
    .required()
    .messages({
      'string.empty': 'Captcha is required',
      'any.required': 'Captcha is required'
    }),
});

export const ChatLayout = () => {
  const [chatDropdown, setchatDropdown] = React.useState(null);
  const chatOpen = Boolean(chatDropdown);

  const handleChatDropdownOpen = (event) => {
    setchatDropdown(event.currentTarget);
  };

  const handleChatDropdownClose = () => {
    setchatDropdown(null);
  };

  const captchaRef = React.useRef(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [lambdaRes, setLambdaRes] = React.useState({ type: "", message: "" });
  
  // Form state
  const [formData, setFormData] = React.useState({
    email: "",
    message: "",
    captcha: ""
  });
  
  // Errors state
  const [errors, setErrors] = React.useState({});
  const validateField = (name, value) => {
    const fieldSchema = Joi.object({ [name]: schema.extract(name) });
    const { error } = fieldSchema.validate({ [name]: value });
    
    if (error) {
      return error.details[0].message;
    }
    return null;
  };

  const validateForm = (data) => {
    const { error } = schema.validate(data, { abortEarly: false });
    
    if (error) {
      const validationErrors = {};
      error.details.forEach((detail) => {
        validationErrors[detail.path[0]] = detail.message;
      });
      return validationErrors;
    }
    return {};
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    const fieldError = validateField(name, value);
    setErrors(prev => ({
      ...prev,
      [name]: fieldError
    }));
  };

  const handleCaptchaError = () => {
    setErrors(prev => ({
      ...prev,
      captcha: "Captcha verification failed"
    }));
  };

  const handleCaptchaExpired = () => {
    setFormData(prev => ({
      ...prev,
      captcha: ""
    }));
    setErrors(prev => ({
      ...prev,
      captcha: "Captcha has expired"
    }));
  };

  const handleCaptchaChange = (value) => {
    setFormData(prev => ({
      ...prev,
      captcha: value || ""
    }));
 
    // Validate captcha
    const fieldError = validateField("captcha", value || "");
    setErrors(prev => ({
      ...prev,
      captcha: fieldError
    }));
  };
 

  const handleSubmit = (event) => {
    event.preventDefault();
    
    const validationErrors = validateForm(formData);
    setErrors(validationErrors);
    
    if (Object.keys(validationErrors).length === 0) {
      handleSubmitForm(formData);
    }
  };

  const handleSubmitForm = async (data) => {
    setIsSubmitting(true);
    try {
      if (data.email && data.message.trim()) {
        const sub = "Texas Solar Pro Q/A Video Library Message";
        const htmlContent = `<html><head><title>Texas Solar Pro Q/A Video Library Message</title></head><body> <div><p>Hi Ryan,</p><p>New Message from Q/A Video Library Section of Texas Solar Pro Website</p><p>Email: ${data.email}</p> <p>Message: ${data.message}</p><p>Thank You!</p></div></body></html>`;

        const input = {
          fromEmail: `Ryan Walllace <${VIDEO_LIBRARY_CHAT_EMAIL}>`,
          toEmail: VIDEO_LIBRARY_CHAT_EMAIL,
          subject: sub,
          html: htmlContent,
          captcha: data.captcha,
        };

        const res = await sendEmailFromVideoLibrary(input);
        if (res.status === 200 && res.data) {
          setLambdaRes((preValue) => ({
            ...preValue,
            type: "success",
            message: "Email Sent Successfully.",
          }));
          // Reset form on success
          setFormData({ email: "", message: "", captcha: "" });
          setErrors({});
          if (captchaRef.current) {
            captchaRef.current.reset();
          }
        } else if (res.status === 400) {
          setLambdaRes((preValue) => ({
            ...preValue,
            type: "error",
            message: "Failed to verify captcha.",
          }));
        } else if (res.status === 500) {
          setLambdaRes((preValue) => ({
            ...preValue,
            type: "error",
            message:
              "Something went wrong while sending Email. Please try again later.",
          }));
        }
      } else {
        setLambdaRes((preValue) => ({
          ...preValue,
          type: "error",
          message: "Email and Message is required",
        }));
      }
    } catch (e) {
      console.log(e)
      setLambdaRes((preValue) => ({
        ...preValue,
        type: "error",
        message: "An unexpected error occurred. Please try again later.",
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="chat-btn-container">
      <Tooltip
        arrow
        classes={{ popper: "custom" }}
        placement="top"
        title={
          <Typography fontSize={16} color={"white"}>
            Have any question that's not answered? Click here.
          </Typography>
        }
      >
        <Badge
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          overlap="circular"
          size={"large"}
          className="chat-badge"
        >
          <IconButton
            size="large"
            className={` round draggable-handle`}
            onClick={handleChatDropdownOpen}
          >
            <LiveHelp fontSize="large" />
          </IconButton>
        </Badge>
      </Tooltip>

      <Popover
        component={"div"}
        anchorEl={chatDropdown}
        open={chatOpen}
        disableScrollLock
        onClose={() => {
          handleChatDropdownClose();
        }}
        className="chat-popover"
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Box m={2}>
          <Typography textAlign={"center"} mb={3} variant="h5">
            If you have a question that is not answered here please let us know.
          </Typography>
          <form onSubmit={handleSubmit} noValidate>
            <TextField
              label="E-mail"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email || ""}
              variant="outlined"
              fullWidth
              required
            />
            <Box variant="container" mt={2} />
            <TextField
              label="Message"
              id="message outlined-multiline-static"
              name="message"
              className="message-textarea"
              value={formData.message}
              onChange={handleChange}
              error={!!errors.message}
              helperText={errors.message || ""}
              variant="outlined"
              fullWidth
              required
              multiline
            />
            <Box variant="container" mt={2} />
            <ReCAPTCHA
              sitekey={process.env.REACT_APP_RECAPTCHA_V2_NEW_SITE_KEY}
              name="captcha"
              onErrored={handleCaptchaError}
              onExpired={handleCaptchaExpired}
              onChange={handleCaptchaChange}
              ref={captchaRef}
            />
            {errors.captcha && (
              <span style={{ color: "#da402f" }}>{errors.captcha}</span>
            )}
            {lambdaRes.type && lambdaRes.message ? (
              <Typography
                mt={2}
                fontSize={16}
                color={lambdaRes.type === "success" ? "green" : "red"}
              >
                {lambdaRes.message}
              </Typography>
            ) : null}
            <Box variant="container" mt={2} />
            <Button
              disabled={isSubmitting}
              startIcon={
                isSubmitting ? (
                  <MuiCircularProgress
                    component={"div"}
                    variant={"indeterminate"}
                    color={"inherit"}
                    size={"1em"}
                  />
                ) : null
              }
              variant="contained"
              color="primary"
              type="submit"
            >
              Submit
            </Button>
          </form>
        </Box>
      </Popover>
    </div>
  );
};

